import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";
import { ContractorsRepository } from "@/api/contractors/repository";
import { ContractorsService } from "@/api/contractors/service";
import { Form, InputNumberControl, SelectControl } from "@/components/controls";
import type { IContractorProfessionalDetailsFormData } from "@/pages/Settings/types";
import type { IContractor } from "@/types/users-contractors";
import { ContractorExpertise } from "@/types/users-contractors";
import type { ISelectOption } from "@/ui-kit";
import { Button, Card, Drawer, Toast } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

const expertiseOptions: ISelectOption[] = [
  {
    id: ContractorExpertise.Carpentry,
    text: ContractorExpertise.Carpentry,
  },
  {
    id: ContractorExpertise.Electrical,
    text: ContractorExpertise.Electrical,
  },
  {
    id: ContractorExpertise.Painting,
    text: ContractorExpertise.Painting,
  },
  {
    id: ContractorExpertise.Plumbing,
    text: ContractorExpertise.Plumbing,
  },
  {
    id: ContractorExpertise.Roofing,
    text: ContractorExpertise.Roofing,
  },
];

type IProps = {
  profile: IContractor;
  onSave: (v: IContractor) => void;
  onDismiss: () => void;
};

const EditModal = ({ profile, onSave, onDismiss }: IProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const schema = useMemo(() => {
    return z.object({
      yearsOfExperience: z
        .string()
        .min(1, { message: t("validation.required") }),
      expertise: z
        .array(
          z.enum(Object.values(ContractorExpertise), {
            message: t("validation.required"),
          }),
        )
        .min(1, { message: t("validation.required") }),
    });
  }, [t]);

  const form = useForm<IContractorProfessionalDetailsFormData>({
    defaultValues: {
      yearsOfExperience: profile.yearsOfExperience.toString() || "",
      expertise: profile.expertise || [],
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit } = form;

  const onSubmit = useCallback(() => {
    handleSubmit((formData: IContractorProfessionalDetailsFormData) => {
      setLoading(true);
      const contractorsRepository = new ContractorsRepository(
        new ContractorsService(),
      );

      return contractorsRepository
        .updateContractorProfessionalDetails({
          userId: profile.userId,
          expertiseList: formData.expertise,
          yearsOfExperience: Number(formData.yearsOfExperience),
        })
        .then((response) => {
          Toast.success({
            title: t("toast.experienceUpdated"),
          });
          onSave(response);
          onDismiss();
        })
        .catch(serverErrorHandler())
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [handleSubmit, profile.userId, t, onSave, onDismiss]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  return (
    <FormProvider {...form}>
      <Drawer
        className="settings__user-modal"
        open={true}
        onOpenChange={onDismiss}
        closeOnOutsidePress={false}
      >
        <Drawer.Header title={t("label.experience")} />
        <Drawer.Body>
          <Form>
            <Card.Body className="onboarding-card-content">
              <InputNumberControl
                label={t("label.years_of_experience")}
                name="yearsOfExperience"
                autoFocus={true}
                maxLength={2}
              />

              <SelectControl
                label={t("label.expertise")}
                options={expertiseOptions}
                name="expertise"
                multiple
              />
            </Card.Body>
          </Form>
        </Drawer.Body>
        <Drawer.Footer>
          <Button onClick={handleDismiss} variant="ghost" color="neutral">
            {t("action.cancel")}
          </Button>
          <Button onClick={onSubmit} loading={loading}>
            {t("action.save")}
          </Button>
        </Drawer.Footer>
      </Drawer>
    </FormProvider>
  );
};

export default EditModal;
